import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import CommonSelect from "../../../../../core/common/commonSelect";
import ImageSelect from "../../../../../core/common/imageSelect";

interface ExportModalProps {
  show: boolean;
  onHide: () => void;
  onExport: (exportConfig: any) => void;
  companies: any[];
  departments: any[];
  employees: any[];
  selectedCompany: any;
  exportLoading: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({
  show,
  onHide,
  onExport,
  companies,
  departments,
  employees,
  selectedCompany,
  exportLoading,
}) => {
  const [exportType, setExportType] = useState<
    "all" | "department" | "individual"
  >("all");
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().startOf("month"),
    endDate: dayjs(),
  });
  const [filteredEmployees, setFilteredEmployees] = useState<any[]>([]);
  const [exportFormat, setExportFormat] = useState<"excel" | "pdf">("excel");
  const [reportDetail, setReportDetail] = useState<"summary" | "complete">(
    "summary"
  );
  const [empStatus, setEmpStatus] = useState<
    "all" | "active" | "inactive" | "suspended"
  >("active");

  // Filter employees based on selected department
  useEffect(() => {
    if (
      exportType === "individual" &&
      selectedDepartment &&
      Array.isArray(employees)
    ) {
      const filtered = employees.filter(
        (emp: any) => emp.department === selectedDepartment.value
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(Array.isArray(employees) ? employees : []);
    }
  }, [exportType, selectedDepartment, employees]);

  // Reset report detail when export type changes
  useEffect(() => {
    if (exportType === "individual") {
      setReportDetail("summary"); // Individual only has summary
    }
  }, [exportType]);

  const handleExport = () => {
    const exportConfig = {
      type: exportType,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      department: selectedDepartment,
      employee: selectedEmployee,
      company: selectedCompany,
      format: exportFormat,
      reportDetail: reportDetail, // Add report detail type
      empStatus: empStatus,
    };
    onExport(exportConfig);
  };

  const handleReset = () => {
    setExportType("all");
    setSelectedDepartment(null);
    setSelectedEmployee(null);
    setDateRange({
      startDate: dayjs().startOf("month"),
      endDate: dayjs(),
    });
    setReportDetail("summary");
    setEmpStatus("active");
  };

  // Helper function to get employee image URL
  const getEmployeeImageUrl = (emp: any) => {
    return emp.profile_pic || emp.Image || "";
  };

  // Employee options for ImageSelect
  const employeeOptions = Array.isArray(filteredEmployees)
    ? filteredEmployees.map((emp: any) => {
        return {
          value: emp.id,
          label:
            `${emp.fname || ""} ${emp.lname || ""}`.trim() ||
            emp.username ||
            "Unknown Employee",
          imageUrl: getEmployeeImageUrl(emp),
        };
      })
    : [];

  const departmentOptions = Array.isArray(departments)
    ? departments.map((dept: any) => ({
        value: dept.value,
        label: dept.label,
      }))
    : [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Export Attendance Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Company Selection */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Company</Form.Label>
              <CommonSelect
                options={companies}
                defaultValue={selectedCompany}
                isDisabled={true}
                placeHolder="Select Company"
              />
            </Col>
            <Col md={6}>
              <Form.Label>Export Format</Form.Label>
              <Form.Select
                value={exportFormat}
                onChange={(e) =>
                  setExportFormat(e.target.value as "excel" | "pdf")
                }
              >
                <option value="excel">Excel (CSV)</option>
                <option value="pdf">PDF</option>
              </Form.Select>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Export Type</Form.Label>
              <Form.Select
                value={exportType}
                onChange={(e) => setExportType(e.target.value as any)}
              >
                <option value="all">All Employees</option>
                <option value="department">By Department</option>
                <option value="individual">Individual Employee</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Employee Status</Form.Label>
              <Form.Select
                value={empStatus}
                onChange={(e) => setEmpStatus(e.target.value as any)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </Form.Select>
            </Col>
          </Row>

          {/* Report Detail Type - Only show for All and Department */}
          {(exportType === "all" || exportType === "department") && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Report Detail</Form.Label>
                <Form.Select
                  value={reportDetail}
                  onChange={(e) =>
                    setReportDetail(e.target.value as "summary" | "complete")
                  }
                >
                  <option value="summary">Summary Report</option>
                  <option value="complete">Complete Detailed Report</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {reportDetail === "summary"
                    ? "Shows summary with total counts and percentages"
                    : "Shows detailed daily attendance for each employee"}
                </Form.Text>
              </Col>
            </Row>
          )}

          {/* Department Selection */}
          {exportType === "department" && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Select Department</Form.Label>
                <CommonSelect
                  options={departmentOptions}
                  defaultValue={selectedDepartment}
                  onChange={setSelectedDepartment}
                  placeHolder="Select Department"
                />
              </Col>
            </Row>
          )}

          {/* Individual Employee Selection */}
          {exportType === "individual" && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Select Department</Form.Label>
                  <CommonSelect
                    options={departmentOptions}
                    defaultValue={selectedDepartment}
                    onChange={setSelectedDepartment}
                    placeHolder="Select Department First"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Select Employee</Form.Label>
                  <ImageSelect
                    options={employeeOptions}
                    defaultValue={selectedEmployee}
                    onChange={setSelectedEmployee}
                    placeHolder={
                      selectedDepartment
                        ? "Select Employee"
                        : "Select Department First"
                    }
                    isDisabled={!selectedDepartment}
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Date Range */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={dateRange.startDate.format("YYYY-MM-DD")}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    startDate: dayjs(e.target.value),
                  }))
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={dateRange.endDate.format("YYYY-MM-DD")}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    endDate: dayjs(e.target.value),
                  }))
                }
              />
            </Col>
          </Row>

          {/* Summary */}
          <div className="alert alert-info">
            <h6>Export Summary:</h6>
            <ul className="mb-0">
              <li>
                Type:{" "}
                {exportType === "all"
                  ? "All Employees"
                  : exportType === "department"
                  ? `Department: ${selectedDepartment?.label || "Not selected"}`
                  : `Individual: ${selectedEmployee?.label || "Not selected"}`}
              </li>
              <li>
                Detail:{" "}
                {exportType === "individual"
                  ? "Summary"
                  : reportDetail === "summary"
                  ? "Summary"
                  : "Complete Detail"}
              </li>
              <li>
                Period: {dateRange.startDate.format("MMM D, YYYY")} -{" "}
                {dateRange.endDate.format("MMM D, YYYY")}
              </li>
              <li>Company: {selectedCompany?.label}</li>
              <li>
                Employee Status:{" "}
                {empStatus.charAt(0).toUpperCase() + empStatus.slice(1)}
              </li>
              <li>Format: {exportFormat.toUpperCase()}</li>
            </ul>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleReset}
          style={{ marginRight: "10px" }}
        >
          Reset
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          style={{ marginRight: "10px" }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={
            exportLoading ||
            !selectedCompany ||
            (exportType === "department" && !selectedDepartment) ||
            (exportType === "individual" &&
              (!selectedDepartment || !selectedEmployee))
          }
        >
          {exportLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Exporting...
            </>
          ) : (
            "Export Report"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportModal;
